import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { APIErrorResponse } from "shared/api/api";
import { useConfigContext } from "shared/contexts/ConfigContext";
import { capitalizeFirstLetter } from "shared/utils";

import Error from "features/ui/Error";

import SupportContent from "./SupportContent";

interface Props {
  error: APIErrorResponse;
  onBadRequest?: () => void;
}

const APIError = ({ error: { message, response }, onBadRequest }: Props) => {
  const { t } = useTranslation("common");
  const { general } = useConfigContext();
  const supportEmail = general?.helpContactEmail;

  useEffect(() => {
    if (response?.status === 400 && onBadRequest) {
      onBadRequest();
    }
    // we need this exception as otherwise it would produce infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  if (response?.status === 400 && response.data?.details) {
    let detailsMessage = "";
    if (typeof response.data.details === "string") {
      detailsMessage = capitalizeFirstLetter(response.data.details as string);
    } else if (
      typeof response.data.details === "object" &&
      "reason" in response.data.details &&
      typeof response.data.details.reason === "string"
    ) {
      detailsMessage = capitalizeFirstLetter(
        response.data.details.reason as string
      );
    }

    return (
      <Error content={t("api_error.bad_request")} moreInfo={detailsMessage} />
    );
  }

  const messageToUse = response?.statusText
    ? `${message}: ${response.statusText}`
    : message;

  const content = supportEmail ? (
    <SupportContent email={supportEmail} />
  ) : undefined;

  return <Error content={content} moreInfo={messageToUse} />;
};

export default APIError;
